<script setup lang="ts">
/**
 * frontend-screens-app-shell-R01…R12; frontend-access-map-R01/R02.
 * AppLayout - Main Application Layout
 *
 * Global layout with header (project) and vertical navigation
 */

import { ref, onMounted, onBeforeUnmount, watch, computed } from "vue";
import { useRouter, useRoute, RouterView } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  Menu,
  Plus,
  Coin,
  Document,
  Connection,
  Switch,
  Operation,
  User,
  SwitchButton,
} from "@element-plus/icons-vue";
import {
  ElAside,
  ElAvatar,
  ElButton,
  ElContainer,
  ElDialog,
  ElDivider,
  ElForm,
  ElFormItem,
  ElHeader,
  ElIcon,
  ElInput,
  ElMain,
  ElMessage,
  ElOption,
  ElPopover,
  ElSelect,
  ElTag,
  ElTooltip,
} from "element-plus";
import { isSection } from "@/shared/config/privileges";
import type { ICreateProjectRequest } from "@/shared/api";
import { createFeatureId } from "@/shared/types";
import MbLanguageSwitcher from "@/shared/ui/atoms/MbLanguageSwitcher.vue";
import { MbUndoRedoButtons, useUndoRedoStore } from "@/features/undo-redo";
import {
  MbFeatureFormDialog,
  MbFeatureManagerDialog,
} from "@/features/feature-management";
import { useApiErrorMessage } from "@/shared/lib";
import { useAuthStore } from "@/entities/auth";
import { useProjectsStore } from "@/entities/projects";
import { useFeaturesStore } from "@/entities/features";
import {
  useBmVersioningStore,
  versioningFailureForDisplay,
  VERSIONING_FAILURE_NAMESPACES,
} from "@/features/bm-versioning";

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
/**
 * Показ отказа по машиночитаемому признаку причины (api-errors-R08): занятое
 * имя проекта, пустое имя и отказ в доступе различимы между собой, потому что
 * различимы их признаки. Действие, которое не выполнено, не остаётся
 * незамеченным (R09).
 */
const { showApiError } = useApiErrorMessage();
// Отказы версионирования: тексты ищутся сперва в пространстве операции
// (`versioning.<операция>Failure.errors.<признак>`), затем в общих
// (api-errors-R04, R08; BUG-0071, BUG-0073).
const { showApiError: showCheckoutError } = useApiErrorMessage(
  VERSIONING_FAILURE_NAMESPACES.checkout
);
const { showApiError: showCommitError } = useApiErrorMessage(
  VERSIONING_FAILURE_NAMESPACES.commit
);
const { showApiError: showHistoryError } = useApiErrorMessage(
  VERSIONING_FAILURE_NAMESPACES.history
);

/**
 * Служебное значение опции «создать…» в селекторах шапки.
 *
 * В состояние выбора оно не попадает никогда: обработчик по нему только
 * открывает диалог (projects-R08, feature-lifecycle-R26).
 */
const CREATE_OPTION_VALUE = "__create_new__";

/**
 * Предел длины сообщения коммита — тот же, что у продукта
 * (business-model-versioning-R05): счётчик и ограничение ввода считают
 * символы, а не байты, поэтому кириллица и латиница упираются в предел
 * одинаково.
 */
const MAX_COMMIT_MESSAGE_LENGTH = 1000;

const authStore = useAuthStore();
const projectsStore = useProjectsStore();
const featuresStore = useFeaturesStore();
const versioningStore = useBmVersioningStore();
const undoRedoStore = useUndoRedoStore();

// Dialogs (UI-only state)
const showCreateProjectDialog = ref(false);
const showCreateFeatureDialog = ref(false);
/** Перечень фич проекта — поверхность управления фичами (feature-lifecycle-R11). */
const showFeatureManagerDialog = ref(false);
const showCommitDialog = ref(false);
const showHistoryDialog = ref(false);
const showCheckoutDialog = ref(false);
const checkoutTargetFeatureId = ref<string>("");
const checkoutBranchName = ref("");
const commitMessage = ref("");
const newProjectForm = ref<ICreateProjectRequest>({
  name: "",
  description: "",
});

// Navigation items (computed to be reactive to language changes).
// Пункты ведут в разделы клиентских путей (app-shell-shell-routing-R04, R10):
// frontend-class-mappings-R02: маппинги — отдельный раздел оболочки.
const navItems = computed(() => [
  {
    id: "source-explorer",
    icon: Coin,
    label: t("nav.sourceExplorer"),
    path: "/",
    visible: authStore.canAccessSection("source-explorer"),
  },
  {
    id: "bm",
    icon: Document,
    label: t("nav.businessLayer"),
    path: "/business-model",
    visible: authStore.canAccessSection("business-model"),
    disabled: false,
  },
  {
    id: "mappings",
    icon: Switch,
    label: t("nav.mappings"),
    path: "/mappings",
    visible: authStore.canAccessSection("mappings"),
  },
  {
    id: "etl",
    icon: Connection,
    label: t("nav.etl"),
    path: "/etl",
    visible: true,
    disabled: true,
  },
].filter(item => item.visible));

/**
 * Разделы, работающие в контексте выбранной фичи: только в них показывается
 * выбор фичи. Из разделов оболочки (app-shell-shell-routing-R04) это
 * бизнес-модель и маппинги.
 */
// business-model-features-R11; feature-lifecycle-R27; versioning-R19.
const FEATURE_CONTEXT_ROUTES: readonly string[] = ["/business-model", "/mappings"];

const sectionAllowed = computed(() => !isSection(route.name) || authStore.canAccessSection(route.name));
const canViewVersions = computed(() => authStore.hasPrivilege("metabridge.versions.view"));
const canEditVersions = computed(() => authStore.hasPrivilege("metabridge.versions.edit"));
const creationOnlyShell = computed(() => !sectionAllowed.value && route.path === '/' &&
  (authStore.hasPrivilege('metabridge.projects.edit') || canEditVersions.value));

const isFeatureContextRoute = computed<boolean>(() =>
  FEATURE_CONTEXT_ROUTES.includes(route.path)
);

// business-model-features-R11; frontend-screens-app-shell-R03/R12:
// load options on project changes and when version viewing becomes available.
watch(() => [projectsStore.selectedProjectId, canViewVersions.value, featuresStore.contextPending] as const, async ([newProjectId, mayViewVersions, contextPending]) => {
  featuresStore.cancelFeatureLoad();
  if (newProjectId.length > 0) {
    if (mayViewVersions && !contextPending) await featuresStore.loadFeatures(newProjectId);
    // Перечень нового проекта пересчитывает выбор фичи (features-R10/R11):
    // фича прежнего проекта выбранной не остаётся, поэтому сюда приходит
    // либо фича текущего проекта, либо пусто; проверка принадлежности —
    // защитная (undo-redo-R11, R16).
    undoRedoStore.setFeatureSelected(
      featuresStore.selectedFeatureId.length > 0,
      isFeatureOfCurrentProject(featuresStore.selectedFeatureId),
    );
  } else {
    featuresStore.clearFeatures();
  }
});

// changelog-undo-redo-edit-sessions-R03/R11; frontend-access-map-R01.
// The grant set can change while the project remains selected. Serialize
// transitions so a slow open cannot leave a session after access is revoked.
let sessionTransition = Promise.resolve();
let shellActive = true;
let sessionRevision = 0;
let sessionStarted = undoRedoStore.hasSession;
function isCurrentSessionTransition(revision: number): boolean {
  return shellActive && revision === sessionRevision;
}
async function closeShellSession(): Promise<void> {
  if (!sessionStarted) return;
  sessionStarted = false;
  await undoRedoStore.destroy();
}
watch(
  () => [projectsStore.selectedProjectId, authStore.canOpenEditSession] as const,
  () => {
    // R11/R16: старый сеанс не предлагает действия во время смены проекта.
    const projectId = projectsStore.selectedProjectId;
    undoRedoStore.setProjectSelected(projectId.length > 0 ? Number(projectId) : null);
    const revision = ++sessionRevision;
    sessionTransition = sessionTransition.then(async () => {
      if (!isCurrentSessionTransition(revision)) return;
      await closeShellSession();
      if (!isCurrentSessionTransition(revision) || !authStore.canOpenEditSession) return;
      const projectId = projectsStore.selectedProjectId;
      if (projectId.length > 0) {
        sessionStarted = true;
        await undoRedoStore.init(Number(projectId));
        if (!isCurrentSessionTransition(revision)) return;
        undoRedoStore.setFeatureSelected(
          featuresStore.selectedFeatureId.length > 0,
          isFeatureOfCurrentProject(featuresStore.selectedFeatureId),
        );
      }
    });
  },
  { immediate: true },
);

/**
 * Принадлежит ли выбранная фича текущему проекту.
 *
 * Перечень фич загружается по проекту, поэтому «фича есть в перечне» и значит
 * «фича этого проекта». Отмена и возврат на странице чужой фичи не
 * предлагаются (undo-redo-R11, R16).
 */
function isFeatureOfCurrentProject(featureId: string): boolean {
  if (featureId.length === 0) return true;
  if (!canViewVersions.value) return featuresStore.projectId === projectsStore.selectedProjectId;
  return featuresStore.features.some((feature) => feature.id === featureId);
}

// Sync feature selection with versioning store
watch(() => [featuresStore.selectedFeatureId, canViewVersions.value] as const, async ([newFeatureId]) => {
  // Отмена и возврат доступны только при выбранной фиче
  // (business-model-versioning-R21, основание: вердикт 232): подстановки
  // фичи по умолчанию нет ни на одной поверхности.
  undoRedoStore.setFeatureSelected(
    newFeatureId.length > 0,
    isFeatureOfCurrentProject(newFeatureId),
  );

  if (newFeatureId.length > 0) {
    versioningStore.setCurrentFeature(createFeatureId(newFeatureId));
    if (!canViewVersions.value) return;
    const loaded = await versioningStore.loadHistory();
    if (!loaded) {
      // Отказ загрузки истории показывается причиной, а не теряется молча
      // (api-errors-R09, R08; BUG-0075).
      showHistoryError(
        versioningFailureForDisplay(versioningStore.error, t("versioning.loadHistoryError"))
      );
    }
  } else {
    versioningStore.setCurrentFeature(null);
  }
}, { immediate: true });

// Load projects on mount
watch(() => authStore.hasPrivilege("metabridge.projects.view"), async allowed => {
  if (allowed) await projectsStore.loadProjects();
}, { immediate: true });
onMounted(() => {
  window.addEventListener("focus", refreshProfile);
});

function refreshProfile(): void { void authStore.refreshUser(); }

// Cleanup edit session on unmount
onBeforeUnmount(() => {
  featuresStore.cancelFeatureLoad();
  shellActive = false;
  window.removeEventListener("focus", refreshProfile);
  void sessionTransition.then(closeShellSession);
});

/**
 * Выбор в селекторе проекта.
 *
 * Норма: spec/backend/administration/projects.md, R08 (основание: вердикт
 * 270) — открытие диалога создания текущий выбор **не меняет**: ни проект, ни
 * зависимый от него контекст (перечень и выбор фичи, сеанс редактирования) не
 * перезагружаются, пока пользователь не завершил диалог. Поэтому служебное
 * значение в состояние выбора не попадает вовсе: оно лишь открывает диалог.
 * Выбранным проект становится только при успешном создании (R08, вторая
 * половина — в редьюсере `CREATE_PROJECT_SUCCESS`).
 */
function handleProjectChange(projectId: string): void {
  if (projectId === CREATE_OPTION_VALUE) {
    openCreateProjectDialog();
    return;
  }
  projectsStore.selectProject(projectId);
}

/**
 * Выбор в селекторе фичи — то же правило.
 *
 * Норма: spec/backend/business-model/feature-lifecycle.md, R26 (основание:
 * вердикт 274, распространение вердикта 270 на второй селектор шапки):
 * открытие диалога создания фичи выбор и зависимый контекст (дерево модели,
 * диаграммы, маппинги, история версий) не меняет. Оба селектора шапки ведут
 * себя одинаково — открытие диалога есть заявление намерения, а не смена
 * рабочего контекста.
 */
function handleFeatureChange(featureId: string): void {
  if (featureId === CREATE_OPTION_VALUE) {
    openCreateFeatureDialog();
    return;
  }
  featuresStore.selectFeature(featureId);
}

// Project dialogs
function openCreateProjectDialog(): void {
  showCreateProjectDialog.value = true;
}

function closeCreateProjectDialog(): void {
  showCreateProjectDialog.value = false;
  newProjectForm.value = { name: "", description: "" };
}

async function handleCreateProject(): Promise<void> {
  await projectsStore.createProject(newProjectForm.value);
  if (projectsStore.error !== null) {
    showApiError(projectsStore.error);
    return;
  }
  closeCreateProjectDialog();
}

// Feature dialogs
function openCreateFeatureDialog(): void {
  if (projectsStore.selectedProjectId.length === 0) {
    ElMessage.warning({ message: t("feature.selectProjectFirst") });
    return;
  }
  showCreateFeatureDialog.value = true;
}

/**
 * Перечень фич проекта: заведение, свойства и удаление фичи живут там
 * (feature-lifecycle-R01, R04, R11). Сам селектор действия удаления не
 * содержит — он выбирает фичу и предлагает её создание (features-R11).
 */
function openFeatureManagerDialog(): void {
  showFeatureManagerDialog.value = true;
}

// Commit handlers
function openCommitDialog(): void {
  commitMessage.value = "";
  showCommitDialog.value = true;
}

function closeCommitDialog(): void {
  showCommitDialog.value = false;
  commitMessage.value = "";
}

async function handleCommit(): Promise<void> {
  const newFeatureId = await versioningStore.commit(
    commitMessage.value || undefined
  );
  if (newFeatureId) {
    // Update selected feature to new HEAD
    featuresStore.selectFeature(String(newFeatureId));
    ElMessage.success({ message: t("versioning.commitSuccess") });
    closeCommitDialog();
    // Reload features list
    if (canViewVersions.value) await featuresStore.loadFeatures(projectsStore.selectedProjectId);
  } else {
    // Пользователь видит причину отказа, а не обобщённое «не удалось»
    // (business-model-versioning-R07, api-errors-R08; BUG-0073).
    showCommitError(
      versioningFailureForDisplay(versioningStore.error, t("versioning.commitError"))
    );
  }
}

// History handlers
function openHistoryDialog(): void {
  showHistoryDialog.value = true;
}

function closeHistoryDialog(): void {
  showHistoryDialog.value = false;
}

function handleCheckout(commitFeatureId: string): void {
  checkoutTargetFeatureId.value = commitFeatureId;
  checkoutBranchName.value = "";
  showCheckoutDialog.value = true;
}

function closeCheckoutDialog(): void {
  showCheckoutDialog.value = false;
  checkoutTargetFeatureId.value = "";
  checkoutBranchName.value = "";
}

async function confirmCheckout(): Promise<void> {
  const branchName = checkoutBranchName.value.trim() || undefined;
  const newFeatureId = await versioningStore.checkout(
    createFeatureId(checkoutTargetFeatureId.value),
    branchName
  );
  if (newFeatureId) {
    // Update selected feature to new branch
    featuresStore.selectFeature(String(newFeatureId));
    ElMessage.success({ message: t("versioning.checkoutSuccess") });
    closeCheckoutDialog();
    closeHistoryDialog();
    // Reload features list
    await featuresStore.loadFeatures(projectsStore.selectedProjectId);
  } else {
    // Пользователь видит причину отказа, а не обобщённое «не удалось»
    // (business-model/versioning.md, api-errors-R08; BUG-0071).
    showCheckoutError(
      versioningFailureForDisplay(versioningStore.error, t("versioning.checkoutError"))
    );
  }
}

// access-and-auth-login-sessions-R10/R11: a single common browser logout.
async function handleLogout(): Promise<void> {
  await authStore.logout();
}

// Provide context to child components
defineExpose({
  selectedProjectId: computed(() => projectsStore.selectedProjectId),
  selectedFeatureId: computed(() => featuresStore.selectedFeatureId),
});
</script>

<template>
  <ElContainer class="app-layout">
    <!-- Global Header -->
    <ElHeader class="app-header">
      <div class="header-left">
        <ElButton :icon="Menu" circle size="default" :title="t('app.menu')" />
        <span class="app-title">{{ t("app.title") }}</span>

        <!-- Project Selector -->
        <ElSelect
          v-if="authStore.hasPrivilege('metabridge.projects.view')"
          :model-value="projectsStore.selectedProjectId"
          :placeholder="t('project.select')"
          size="default"
          class="header-selector"
          @update:model-value="handleProjectChange"
        >
          <ElOption
            v-for="project in projectsStore.projects"
            :key="project.id"
            :label="project.name"
            :value="project.id"
          />
          <ElDivider class="selector-divider" />
          <ElOption v-if="authStore.hasPrivilege('metabridge.projects.edit')" :value="CREATE_OPTION_VALUE">
            <ElIcon><Plus /></ElIcon>
            <span class="option-text">{{ t("project.create") }}</span>
          </ElOption>
        </ElSelect>

        <ElButton
          v-else-if="authStore.hasPrivilege('metabridge.projects.edit')"
          :aria-label="t('project.createStandalone')"
          @click="openCreateProjectDialog"
        >{{ t('project.createStandalone') }}</ElButton>

        <!--
          Выбор фичи — в разделах, работающих в контексте фичи
          (FEATURE_CONTEXT_ROUTES): бизнес-модель.
        -->
        <ElSelect
          v-if="projectsStore.selectedProjectId && isFeatureContextRoute && canViewVersions"
          :model-value="featuresStore.selectedFeatureId"
          :placeholder="t('feature.select')"
          size="default"
          class="header-selector"
          @update:model-value="handleFeatureChange"
        >
          <!--
            Опции: фичи перечня плюс архивная выбранная — она встречается в
            селекторе единственным образом, как выбранная на момент архивации
            (features-R08, R11). Пометка статуса: `merged` — без пометки,
            `archived` — нейтральная, остальные — предупреждающая (R11).
          -->
          <ElOption
            v-for="feature in featuresStore.selectorOptions"
            :key="feature.id"
            :label="feature.name"
            :value="feature.id"
          >
            <span>{{ feature.name }}</span>
            <ElTag
              v-if="feature.status !== 'merged'"
              size="small"
              :type="feature.status === 'archived' ? 'info' : 'warning'"
              class="feature-status-tag"
            >
              {{ t(`feature.statuses.${feature.status}`) }}
            </ElTag>
          </ElOption>
          <ElDivider class="selector-divider" />
          <ElOption v-if="canEditVersions" :value="CREATE_OPTION_VALUE">
            <ElIcon><Plus /></ElIcon>
            <span class="option-text">{{ t("feature.create") }}</span>
          </ElOption>
        </ElSelect>

        <ElButton
          v-if="projectsStore.selectedProjectId && !canViewVersions && canEditVersions && !featuresStore.contextPending"
          :aria-label="t('feature.createStandalone')"
          @click="openCreateFeatureDialog"
        >{{ t('feature.createStandalone') }}</ElButton>

        <!--
          Вход в перечень фич проекта: заведение, свойства и удаление фичи
          (feature-lifecycle-R01, R04, R11).
        -->
        <ElTooltip
          v-if="projectsStore.selectedProjectId && isFeatureContextRoute && canViewVersions"
          :content="t('feature.manageTitle')" placement="bottom"
        >
          <ElButton
            size="default"
            :aria-label="t('feature.manageTitle')"
            @click="openFeatureManagerDialog"
          >
            <ElIcon><Operation /></ElIcon>
          </ElButton>
        </ElTooltip>

        <!-- Versioning Controls -->
        <ElButton
          v-if="projectsStore.selectedProjectId && isFeatureContextRoute && canEditVersions"
          type="primary"
          size="default"
          :disabled="!featuresStore.selectedFeatureId || versioningStore.isCommitting"
          :loading="versioningStore.isCommitting"
          @click="openCommitDialog"
        >
          {{ t("versioning.commit") }}
        </ElButton>

        <ElButton
          v-if="projectsStore.selectedProjectId && isFeatureContextRoute && canViewVersions"
          size="default"
          :disabled="versioningStore.isLoadingHistory"
          @click="openHistoryDialog"
        >
          {{ t("versioning.history") }}
          <ElTag
            v-if="versioningStore.commitCount > 0"
            size="small"
            type="info"
            class="history-count-tag"
          >
            {{ versioningStore.commitCount }}
          </ElTag>
        </ElButton>
      </div>

      <div class="header-right">
        <!-- Undo/Redo Buttons -->
        <MbUndoRedoButtons />
        <!-- Language Selector -->
        <MbLanguageSwitcher />
      </div>
    </ElHeader>

    <ElContainer class="app-content">
      <!-- Vertical Navigation Sidebar -->
      <ElAside width="60px" class="nav-sidebar">
        <!-- Top navigation items -->
        <div class="nav-items-top">
          <ElTooltip
            v-for="item in navItems"
            :key="item.id"
            :content="item.label"
            placement="right"
          >
            <div
              class="nav-item"
              :class="{
                active: route.path === item.path,
                disabled: item.disabled,
              }"
              @click="!item.disabled && router.push(item.path)"
            >
              <ElIcon size="24">
                <component :is="item.icon" />
              </ElIcon>
            </div>
          </ElTooltip>
        </div>

        <!-- User button at bottom -->
        <div class="nav-items-bottom">
          <ElPopover placement="right-end" :width="220" trigger="click">
            <template #reference>
              <button type="button" class="nav-item user-button" :aria-label="t('auth.userMenu')">
                <ElAvatar :size="32">
                  <ElIcon><User /></ElIcon>
                </ElAvatar>
              </button>
            </template>

            <!-- User menu content -->
            <div class="user-menu">
              <div class="user-info">
                <div class="user-login">@{{ authStore.currentUserLogin }}</div>
                <div class="user-name">
                  {{ authStore.currentUser?.fullName }}
                </div>
              </div>

              <ElDivider class="user-menu-divider" />

              <div class="user-menu-items">
                <button type="button" class="menu-item danger" @click="handleLogout">
                  <ElIcon><SwitchButton /></ElIcon>
                  <span>{{ t("auth.logout") }}</span>
                </button>
              </div>
            </div>
          </ElPopover>
        </div>
      </ElAside>

      <!-- Main Content Area -->
      <ElMain class="app-main">
        <p v-if="!sectionAllowed && !creationOnlyShell" role="alert">{{ t("errors.HTTP_403") }}</p>
        <RouterView v-else-if="sectionAllowed && !featuresStore.contextPending" v-slot="{ Component }">
          <component
            :is="Component"
            :project-id="projectsStore.selectedProjectId"
            :feature-id="featuresStore.selectedFeatureId"
          />
        </RouterView>
      </ElMain>
    </ElContainer>

    <!-- Create Project Dialog -->
    <ElDialog
      v-if="authStore.hasPrivilege('metabridge.projects.edit')"
      v-model="showCreateProjectDialog"
      :title="t('project.createTitle')"
      width="500px"
      @close="closeCreateProjectDialog"
    >
      <ElForm
        :model="newProjectForm"
        :label-width="120"
        @submit.prevent="handleCreateProject"
      >
        <ElFormItem :label="t('project.name')" required>
          <ElInput
            v-model="newProjectForm.name"
            :placeholder="t('project.namePlaceholder')"
          />
        </ElFormItem>
        <ElFormItem :label="t('project.description')">
          <ElInput
            v-model="newProjectForm.description"
            type="textarea"
            :rows="3"
            :placeholder="t('project.descriptionPlaceholder')"
          />
        </ElFormItem>
      </ElForm>
      <template #footer>
        <ElButton
          @click="closeCreateProjectDialog"
          :disabled="projectsStore.isCreating"
        >
          {{ t("common.cancel") }}
        </ElButton>
        <ElButton
          type="primary"
          @click="handleCreateProject"
          :loading="projectsStore.isCreating"
          :disabled="!newProjectForm.name"
        >
          {{ t("common.create") }}
        </ElButton>
      </template>
    </ElDialog>

    <!--
      Заведение фичи из селектора шапки и перечень фич проекта —
      feature-lifecycle-R01, R11.
    -->
    <MbFeatureFormDialog
      v-if="canEditVersions"
      v-model="showCreateFeatureDialog"
      :project-id="projectsStore.selectedProjectId"
      :can-read-list="canViewVersions"
    />

    <MbFeatureManagerDialog
      v-if="canViewVersions"
      v-model="showFeatureManagerDialog"
      :project-id="projectsStore.selectedProjectId"
    />

    <!-- Commit Dialog -->
    <ElDialog
      v-if="canEditVersions"
      v-model="showCommitDialog"
      :title="t('versioning.commitTitle')"
      width="500px"
      @close="closeCommitDialog"
    >
      <ElForm @submit.prevent="handleCommit">
        <!--
          Предел сообщения — 1000 символов независимо от алфавита
          (business-model-versioning-R05, основание: вердикт 231). Поле не
          позволяет ввести больше и показывает счётчик, поэтому отказ по
          длине из интерфейса недостижим; превышение иным путём отклоняет
          продукт.
        -->
        <ElFormItem :label="t('versioning.commitMessage')">
          <ElInput
            v-model="commitMessage"
            type="textarea"
            :rows="3"
            :maxlength="MAX_COMMIT_MESSAGE_LENGTH"
            show-word-limit
            :placeholder="t('versioning.commitMessagePlaceholder')"
          />
        </ElFormItem>
      </ElForm>
      <template #footer>
        <ElButton
          @click="closeCommitDialog"
          :disabled="versioningStore.isCommitting"
        >
          {{ t("common.cancel") }}
        </ElButton>
        <ElButton
          type="primary"
          @click="handleCommit"
          :loading="versioningStore.isCommitting"
        >
          {{ t("versioning.commit") }}
        </ElButton>
      </template>
    </ElDialog>

    <!-- History Dialog -->
    <ElDialog
      v-if="canViewVersions"
      v-model="showHistoryDialog"
      :title="t('versioning.historyTitle')"
      width="600px"
      @close="closeHistoryDialog"
    >
      <div v-if="versioningStore.isLoadingHistory" class="history-loading">
        {{ t("common.loading") }}
      </div>
      <!-- История выбранной фичи не загружена (отказ показан сообщением):
           это не пустая история (api-errors-R09; BUG-0075). -->
      <div
        v-else-if="versioningStore.currentFeatureId !== null && versioningStore.history === null"
        class="history-empty"
      >
        {{ t("versioning.loadHistoryError") }}
      </div>
      <div v-else-if="versioningStore.commitCount === 0" class="history-empty">
        {{ t("versioning.noHistory") }}
      </div>
      <ul v-else class="history-list">
        <li
          v-for="commit in versioningStore.history?.commits ?? []"
          :key="commit.featureId"
          class="history-item"
          :class="{ 'history-item--head': commit.isHead }"
        >
          <div class="history-item__content">
            <div class="history-item__header">
              <span class="history-item__name">{{ commit.name }}</span>
              <ElTag v-if="commit.isHead" size="small" type="success">
                HEAD
              </ElTag>
            </div>
            <div class="history-item__message">
              {{ commit.message || t("versioning.noMessage") }}
            </div>
            <div class="history-item__meta">
              <span v-if="commit.committedAt">
                {{ new Date(commit.committedAt).toLocaleString() }}
              </span>
              <span v-if="commit.committedByName">
                {{ t("versioning.by") }} {{ commit.committedByName }}
              </span>
            </div>
          </div>
          <div class="history-item__actions">
            <!-- Ветка создаётся от любой фичи — и от рабочей HEAD
                 (business-model-versioning-R08) -->
            <ElButton
              size="small"
              v-if="canEditVersions"
              @click="handleCheckout(String(commit.featureId))"
              :loading="versioningStore.isCheckingOut"
            >
              {{ t("versioning.checkout") }}
            </ElButton>
          </div>
        </li>
      </ul>
      <template #footer>
        <ElButton @click="closeHistoryDialog">
          {{ t("common.close") }}
        </ElButton>
      </template>
    </ElDialog>

    <!-- Checkout Dialog -->
    <ElDialog
      v-if="canEditVersions"
      v-model="showCheckoutDialog"
      :title="t('versioning.checkoutTitle')"
      width="400px"
      @close="closeCheckoutDialog"
    >
      <ElForm @submit.prevent="confirmCheckout">
        <ElFormItem :label="t('versioning.branchName')">
          <ElInput
            v-model="checkoutBranchName"
            :placeholder="t('versioning.branchNamePlaceholder')"
            autofocus
          />
        </ElFormItem>
        <p class="checkout-hint">
          {{ t("versioning.branchNameHint") }}
        </p>
      </ElForm>
      <template #footer>
        <ElButton @click="closeCheckoutDialog">
          {{ t("common.cancel") }}
        </ElButton>
        <ElButton
          type="primary"
          :loading="versioningStore.isCheckingOut"
          @click="confirmCheckout"
        >
          {{ t("versioning.checkout") }}
        </ElButton>
      </template>
    </ElDialog>
  </ElContainer>
</template>

<style scoped>
.app-layout {
  height: 100vh;
  flex-direction: column;
}

/* Header */
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background-color: #fff;
  border-bottom: 1px solid var(--el-border-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  height: 60px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.app-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  margin-right: 8px;
}

.header-selector {
  width: 200px;
}

/* BUG-0141; frontend-class-mappings-R02/R24: keep every header action in the viewport. */
@media (max-width: 1100px) {
  .header-left { gap: 8px; min-width: 0; }
  .header-selector { width: 180px; }
  .header-right { flex-shrink: 0; }
}

/* Content Container */
.app-content {
  height: calc(100vh - 60px);
}

/* Vertical Navigation Sidebar */
.nav-sidebar {
  width: 60px !important;
  background-color: #2c3e50;
  border-right: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.nav-items-top {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 8px;
}

.nav-items-bottom {
  padding: 12px 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.nav-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  border-radius: 8px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s;
}

.nav-item:hover:not(.disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.nav-item.active {
  background-color: var(--el-color-primary);
  color: #fff;
}

.nav-item.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-item.user-button {
  border: 0;
  background: transparent;
  color: #fff;
}

/* Admin navigation section */
.nav-admin-section {
  margin-bottom: 8px;
}

.nav-admin-divider {
  height: 1px;
  background-color: rgba(255, 255, 255, 0.15);
  margin: 8px 4px;
}

/* User Menu */
.user-menu {
  padding: 8px 0;
}

.user-info {
  padding: 0 12px 8px;
}

.user-login {
  font-size: 14px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.user-name {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-top: 2px;
}

.user-menu-items {
  display: flex;
  flex-direction: column;
}

.menu-item {
  border: 0;
  background: transparent;
  width: 100%;
  text-align: left;
  font: inherit;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 14px;
}

.menu-item:hover {
  background-color: var(--el-fill-color-light);
}

.menu-item.danger {
  color: var(--el-color-danger);
}

.menu-item.danger:hover {
  background-color: var(--el-color-danger-light-9);
}

/* Main Content */
.app-main {
  padding: 0;
  background-color: #f5f7fa;
  overflow: hidden;
}

/* Selector styles */
.selector-divider {
  margin: 4px 0;
}

.option-text {
  margin-left: 8px;
}

.feature-status-tag {
  margin-left: 8px;
}

/* User menu styles */
.admin-tag {
  margin-top: 4px;
}

.user-menu-divider {
  margin: 12px 0;
}

/* History count tag */
.history-count-tag {
  margin-left: 4px;
}

/* History dialog */
.history-loading,
.history-empty {
  text-align: center;
  padding: 24px;
  color: var(--el-text-color-secondary);
}

.history-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 400px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  transition: background-color 0.2s;
}

.history-item:hover {
  background-color: var(--el-fill-color-light);
}

.history-item:last-child {
  border-bottom: none;
}

.history-item--head {
  background-color: var(--el-color-success-light-9);
}

.history-item__content {
  flex: 1;
  min-width: 0;
}

.history-item__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.history-item__name {
  font-weight: 500;
  color: var(--el-text-color-primary);
}

.history-item__message {
  font-size: 13px;
  color: var(--el-text-color-regular);
  margin-bottom: 4px;
}

.history-item__meta {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  display: flex;
  gap: 12px;
}

.history-item__actions {
  flex-shrink: 0;
  margin-left: 12px;
}

/* Checkout dialog */
.checkout-hint {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin: 0;
}
</style>
